/**
 * BookingDetailModal - Full-featured booking details modal with inline editing
 * Refactored to use modular sub-components for better maintainability
 * 
 * Features:
 * - View booking details
 * - Edit passengers (count + names)
 * - Edit itinerary (pickup/dropoff addresses) with Google Places autocomplete
 * - Change vehicle category (loaded from tariffs API)
 * - Real-time price recalculation
 * - Payment handling (show paid amount + balance)
 * - Modification/Cancellation requests
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { 
  X, Calendar, CheckCircle2, Loader2, XCircle, AlertCircle, MessageSquare
} from 'lucide-react';

// Sub-components
import {
  BookingPriceSection,
  BookingItinerarySection,
  BookingPassengerSection,
  BookingVehicleSection,
  BookingContactForm,
  getStatusConfig,
  formatDate,
  formatTime,
  DRIVER_ACCEPTED_STATUSES
} from './booking';
import config from 'config';

const API_URL = config.API_URL;

const BookingDetailModal = ({ booking, onClose, onRefresh }) => {
  // Early return MUST be before any hooks - use empty defaults
  const safeBooking = booking || {};
  
  // Helper function to extract data from booking
  const extractBookingData = useCallback((b) => ({
    passengers: b?.passenger_count || b?.passengers || 1,
    passengerNames: b?.passenger_names || [],
    luggage: b?.luggage_count || b?.luggage || 0,
    pickupAddress: b?.itinerary?.pickup_address || b?.pickup?.address || b?.pickup_address || '',
    dropoffAddress: b?.itinerary?.dropoff_address || b?.dropoff?.address || b?.dropoff_address || '',
    pickupCoords: { lat: b?.pickup?.latitude, lng: b?.pickup?.longitude },
    dropoffCoords: { lat: b?.dropoff?.latitude, lng: b?.dropoff?.longitude },
    vehicleCategory: b?.vehicle_category || b?.vehicle_type || 'berline',
    flightNumber: b?.flight_number || '',
    departureFlightNumber: b?.departure_flight_number || '',
    trainNumber: b?.train_number || '',
    departureTrainNumber: b?.departure_train_number || ''
  }), []);
  
  // State for editing
  const [editMode, setEditMode] = useState(null); // 'passengers' | 'itinerary' | 'vehicle' | 'luggage' | null
  const [editData, setEditData] = useState(() => extractBookingData(safeBooking));
  
  const [newPrice, setNewPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // State for contact form (when modifications not allowed)
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactLoading, setContactLoading] = useState(false);

  // Original values for comparison
  const originalData = useRef(extractBookingData(safeBooking));
  
  // Reset state when booking changes (important for modal reuse)
  useEffect(() => {
    if (booking) {
      const newData = extractBookingData(booking);
      setEditData(newData);
      originalData.current = {
        passengers: newData.passengers,
        luggage: newData.luggage,
        pickupAddress: newData.pickupAddress,
        dropoffAddress: newData.dropoffAddress,
        vehicleCategory: newData.vehicleCategory,
        pickupCoords: newData.pickupCoords,
        dropoffCoords: newData.dropoffCoords
      };
      // Reset other states
      setNewPrice(null);
      setHasChanges(false);
      setMessage(null);
      setEditMode(null);
    }
  }, [booking, extractBookingData]);

  // Check for changes - defined before useEffect
  const checkChanges = useCallback(() => {
    const orig = originalData.current;
    const changed = 
      editData.passengers !== orig.passengers ||
      editData.luggage !== orig.luggage ||
      (editData.pickupAddress || '').trim() !== (orig.pickupAddress || '').trim() ||
      (editData.dropoffAddress || '').trim() !== (orig.dropoffAddress || '').trim() ||
      editData.vehicleCategory !== orig.vehicleCategory ||
      // Also check if coordinates changed (important for price calculation)
      editData.pickupCoords?.lat !== orig.pickupCoords?.lat ||
      editData.pickupCoords?.lng !== orig.pickupCoords?.lng ||
      editData.dropoffCoords?.lat !== orig.dropoffCoords?.lat ||
      editData.dropoffCoords?.lng !== orig.dropoffCoords?.lng;
    setHasChanges(changed);
    return changed;
  }, [editData]);

  useEffect(() => {
    checkChanges();
  }, [editData, checkChanges]);

  // Early return AFTER all hooks
  if (!booking) return null;
  
  // Check if modifications are allowed
  const isClosed = ['C', '9', 'cancelled', 'completed'].includes(booking.status) ||
                   ['C', '9'].includes(booking.status_code);
  const isCancelled = ['A', 'cancelled'].includes(booking.status) ||
                      ['A'].includes(booking.status_code);
  
  // Check if driver has accepted the mission
  const statusCode = booking.status_code || booking.status;
  const isDriverAccepted = DRIVER_ACCEPTED_STATUSES.includes(statusCode) ||
                           parseInt(statusCode) >= 5;
  
  // Can only edit if not closed, not cancelled, AND driver hasn't accepted yet
  const canEdit = !isClosed && !isCancelled && !isDriverAccepted;
  const canCancel = !isClosed && !isCancelled && !isDriverAccepted;

  // Calculate new price
  const calculatePrice = async () => {
    if (!hasChanges) return;
    
    setPriceLoading(true);
    setMessage(null);
    
    try {
      // Ensure we have valid coordinates
      const pickupLat = editData.pickupCoords?.lat || 0;
      const pickupLng = editData.pickupCoords?.lng || 0;
      const dropoffLat = editData.dropoffCoords?.lat || 0;
      const dropoffLng = editData.dropoffCoords?.lng || 0;
      
      // Format pickup_datetime properly
      let pickupDatetime = booking.pickup_datetime;
      if (!pickupDatetime) {
        pickupDatetime = new Date(Date.now() + 86400000).toISOString();
      }
      
      const response = await axios.post(`${API_URL}/api/pricing/calculate`, {
        pickup_address: editData.pickupAddress,
        pickup_lat: pickupLat,
        pickup_lng: pickupLng,
        dropoff_address: editData.dropoffAddress,
        dropoff_lat: dropoffLat,
        dropoff_lng: dropoffLng,
        passengers: editData.passengers,
        luggage: editData.luggage,
        vehicle_category: editData.vehicleCategory,
        service_type: booking.service_type || 'transfer',
        pickup_datetime: pickupDatetime
      });
      
      if (response.data) {
        const priceDetails = response.data.price_details || response.data;
        setNewPrice({
          total: priceDetails.total_ttc || priceDetails.total || 0,
          breakdown: priceDetails,
          distance_km: priceDetails.inputs?.route?.estimated_distance_km || priceDetails.distance_km
        });
      }
    } catch (err) {
      console.error('Price calculation error:', err);
      const errorMsg = err.response?.data?.detail || 'Erreur lors du calcul du tarif';
      setMessage({ type: 'error', text: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg) });
    } finally {
      setPriceLoading(false);
    }
  };

  // Save modifications
  const saveModifications = async () => {
    if (!hasChanges || !newPrice) return;
    
    setSaveLoading(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Prepare coordinates - only send if valid
      const pickupCoords = (editData.pickupCoords?.lat && editData.pickupCoords?.lng) 
        ? { lat: editData.pickupCoords.lat, lng: editData.pickupCoords.lng }
        : null;
      const dropoffCoords = (editData.dropoffCoords?.lat && editData.dropoffCoords?.lng)
        ? { lat: editData.dropoffCoords.lat, lng: editData.dropoffCoords.lng }
        : null;
      
      await axios.patch(
        `${API_URL}/api/bookings/${booking.id}/client-update`,
        {
          passenger_count: editData.passengers,
          passenger_names: editData.passengerNames?.length > 0 ? editData.passengerNames : null,
          luggage_count: editData.luggage,
          pickup_address: editData.pickupAddress,
          dropoff_address: editData.dropoffAddress,
          pickup_coords: pickupCoords,
          dropoff_coords: dropoffCoords,
          vehicle_category: editData.vehicleCategory,
          flight_number: editData.flightNumber || null,
          departure_flight_number: editData.departureFlightNumber || null,
          train_number: editData.trainNumber || null,
          departure_train_number: editData.departureTrainNumber || null,
          new_price: newPrice.total,
          price_details: newPrice.breakdown || null
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      setMessage({ type: 'success', text: 'Modifications enregistrées avec succès !' });
      setEditMode(null);
      setNewPrice(null);
      setHasChanges(false);
      
      // Update original data reference
      originalData.current = { ...editData };
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      let errorText = 'Erreur lors de la sauvegarde';
      const detail = err.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorText = detail;
        } else if (Array.isArray(detail)) {
          errorText = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorText = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      setMessage({ type: 'error', text: errorText });
    } finally {
      setSaveLoading(false);
    }
  };

  // Reset changes
  const resetChanges = () => {
    const orig = originalData.current;
    setEditData(prev => ({
      ...prev,
      passengers: orig.passengers,
      luggage: orig.luggage,
      pickupAddress: orig.pickupAddress,
      dropoffAddress: orig.dropoffAddress,
      vehicleCategory: orig.vehicleCategory
    }));
    setNewPrice(null);
    setEditMode(null);
    setMessage(null);
  };

  // Payment
  const handlePayByCard = async () => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { booking_id: booking.id, origin_url: window.location.origin },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors du paiement' });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Cancellation
  const handleCancellation = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) return;
    
    setCancellationLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/bookings/${booking.id}/request-cancellation`,
        { reason: 'Demande client' },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMessage({ type: 'success', text: 'Demande d\'annulation envoyée' });
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur' });
    } finally {
      setCancellationLoading(false);
    }
  };

  // Contact support (when driver has accepted - no more self-service)
  const handleContactSupport = async () => {
    if (!contactMessage.trim()) {
      setMessage({ type: 'error', text: 'Veuillez saisir votre message' });
      return;
    }
    
    setContactLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/bookings/${booking.id}/request-modification`,
        { details: contactMessage },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMessage({ type: 'success', text: 'Votre demande a été envoyée. Nous vous recontacterons rapidement.' });
      setShowContactForm(false);
      setContactMessage('');
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'envoi' });
    } finally {
      setContactLoading(false);
    }
  };

  const statusConfig = getStatusConfig(booking.status_code, booking.status);

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 sm:p-4" 
      data-testid="booking-detail-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#1F1F1F] rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border-t sm:border border-[#C9A24D]/20 animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300">
        {/* Header - Mobile optimized */}
        <div className="sticky top-0 bg-[#1F1F1F] border-b border-[#C9A24D]/20 p-3 sm:p-4 z-10">
          {/* Mobile drag handle */}
          <div className="sm:hidden w-12 h-1 bg-[#C9A24D]/30 rounded-full mx-auto mb-3" />
          
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase">Réservation</p>
              <p className="text-[#C9A24D] font-mono text-base sm:text-lg font-bold truncate" data-testid="booking-reference">
                {booking.booking_number || booking.id?.slice(-8).toUpperCase()}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${statusConfig.color} ${statusConfig.textColor}`} data-testid="booking-status">
                {statusConfig.label}
              </span>
              <button 
                onClick={onClose} 
                className="p-1.5 sm:p-2 hover:bg-[#C9A24D]/10 rounded-lg transition-colors" 
                data-testid="close-modal-btn"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-[#E5E5E5]/60" />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Mobile optimized spacing */}
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
          {/* Message */}
          {message && (
            <div className={`flex items-center gap-2 p-2.5 sm:p-3 rounded-lg text-xs sm:text-sm ${
              message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`} data-testid="message-alert">
              {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
              <span className="line-clamp-2">{message.text}</span>
            </div>
          )}

          {/* Alert: Driver has accepted - Contact required for modifications */}
          {isDriverAccepted && !isClosed && !isCancelled && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-amber-400 font-medium text-xs sm:text-sm">Chauffeur confirmé</p>
                  <p className="text-amber-300/80 text-[10px] sm:text-xs mt-1">
                    Votre chauffeur a accepté cette mission. Pour toute modification, contactez-nous.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setShowContactForm(true)}
                    className="mt-2 sm:mt-3 bg-amber-500 hover:bg-amber-600 text-black font-medium text-xs h-8"
                    data-testid="contact-support-btn"
                  >
                    <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                    Nous contacter
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Contact Form */}
          <BookingContactForm
            show={showContactForm}
            onClose={() => { setShowContactForm(false); setContactMessage(''); }}
            contactMessage={contactMessage}
            setContactMessage={setContactMessage}
            onSubmit={handleContactSupport}
            loading={contactLoading}
          />

          {/* Date & Time (not editable) */}
          <div className="bg-[#0B0B0B] rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase">Date de prise en charge</p>
                <p className="text-[#E5E5E5] text-sm sm:text-base" data-testid="pickup-datetime">
                  {formatDate(booking.pickup_datetime)} à {formatTime(booking.pickup_datetime)}
                </p>
              </div>
            </div>
          </div>

          {/* Itinerary Section */}
          <BookingItinerarySection
            editData={editData}
            setEditData={setEditData}
            editMode={editMode}
            setEditMode={setEditMode}
            canEdit={canEdit}
            booking={booking}
          />

          {/* Passengers & Luggage Section */}
          <BookingPassengerSection
            editData={editData}
            setEditData={setEditData}
            editMode={editMode}
            setEditMode={setEditMode}
            canEdit={canEdit}
          />

          {/* Vehicle Section */}
          <BookingVehicleSection
            editData={editData}
            setEditData={setEditData}
            editMode={editMode}
            setEditMode={setEditMode}
            canEdit={canEdit}
          />

          {/* Price Section */}
          <BookingPriceSection
            booking={booking}
            isCancelled={isCancelled}
            isClosed={isClosed}
            hasChanges={hasChanges}
            newPrice={newPrice}
            priceLoading={priceLoading}
            saveLoading={saveLoading}
            paymentLoading={paymentLoading}
            onCalculatePrice={calculatePrice}
            onSaveModifications={saveModifications}
            onResetChanges={resetChanges}
            onPayByCard={handlePayByCard}
          />

          {/* Cancel button */}
          {canCancel && !hasChanges && !booking.cancellation_requested && (
            <Button
              onClick={handleCancellation}
              disabled={cancellationLoading}
              variant="outline"
              className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 text-sm h-10"
              data-testid="cancel-booking-btn"
            >
              {cancellationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Annuler la réservation
            </Button>
          )}
          
          {/* Contact button when driver accepted (can't self-cancel) */}
          {isDriverAccepted && !isClosed && !isCancelled && !showContactForm && (
            <Button
              onClick={() => setShowContactForm(true)}
              variant="outline"
              className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-xs sm:text-sm h-10"
              data-testid="request-modification-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Demander une modification ou annulation</span>
              <span className="sm:hidden">Modifier / Annuler</span>
            </Button>
          )}
        </div>

        {/* Footer - Mobile safe area */}
        <div className="sticky bottom-0 bg-[#1F1F1F] border-t border-[#C9A24D]/20 p-3 sm:p-4 pb-safe">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="w-full border-[#C9A24D]/30 text-[#E5E5E5]/60 hover:text-[#C9A24D] h-10 sm:h-11" 
            data-testid="close-footer-btn"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal;
